import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, Printer, FileText, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { configAPI, paymentAPI, getPublicReceiptUrl } from '../../services/api';
import { paidByLabel } from '../../utils/paidByLabel';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = d => d ? format(new Date(d), 'dd-MMM-yyyy') : '—';
const toInput = d => d ? format(new Date(d), 'yyyy-MM-dd') : '';

const COMPANY_NAME    = '';
const COMPANY_ADDRESS = '';
const COMPANY_PHONE   = '';
const COMPANY_GSTIN   = '';

const SERVICE_LABELS = {
  broadband_renewal: 'RENEWAL',
  catv_renewal:      'RENEWAL',
  installation:      'Installation Charge',
  security_deposit:  'Security Deposit',
  other:             'Other',
};

export default function BillPrintModal({ payment, customer: custProp, onClose }) {
  const customer = payment?.customer || custProp || {};

  const [companyInfo, setCompanyInfo] = useState(null);
  useEffect(() => { configAPI.get().then(({ data }) => setCompanyInfo(data.data)).catch(() => {}); }, []);

  // CableTV and Broadband are billed as separate businesses — pick the matching identity.
  const serviceType = payment?.paymentFor === 'broadband_renewal' ? 'broadband'
    : payment?.paymentFor === 'catv_renewal' ? 'catv'
    : (customer?.customerType === 'broadband' ? 'broadband' : 'catv');
  const co = companyInfo?.[serviceType] || {};
  const displayName    = co.name    || COMPANY_NAME;
  const displayAddress = co.address || COMPANY_ADDRESS;
  const displayPhone   = co.phone   || COMPANY_PHONE;
  const displayGstin   = co.gstin   || COMPANY_GSTIN;

  const inferredValidTo =
    payment?.periodTo ||
    custProp?.broadband?.expiryDate ||
    custProp?.catv?.expiryDate ||
    null;

  const [validFrom, setValidFrom] = useState(toInput(payment?.periodFrom || null));
  const [validTo,   setValidTo]   = useState(toInput(inferredValidTo));

  const amount      = payment?.amount      ?? 0;
  const discount    = payment?.discount    ?? 0;
  const gstAmount   = payment?.gstAmount   ?? 0;
  const gstPercent  = payment?.gstPercent  ?? 0;
  const totalAmount = payment?.totalAmount ?? (amount - discount + gstAmount);
  const planName    = payment?.planSnapshot?.name || custProp?.broadband?.plan?.name || custProp?.catv?.package?.name || '';
  const planSpeed   = payment?.planSnapshot?.speed || '';

  // Both services are rendered server-side by the same shared template (utils/billRenderer.js
  // buildInvoiceHtml, the AIS-branded "Sales Reciept" layout — CATV and Broadband merged into
  // one design 2026-07-13) — the on-screen mock-up below is a different, older layout that was
  // never updated to match it. Rather than keep it diverging from the real document, fetch the
  // actual PDF as a blob (so it carries the auth header) and preview that instead, whenever a
  // saved payment exists. The mock-up below is now only a fallback for an unsaved/draft bill
  // (no payment._id yet to fetch a PDF for), for either service.
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  useEffect(() => {
    if (!payment?._id) { setPdfPreviewUrl(null); return; }
    let objectUrl;
    paymentAPI.getReceiptBlob(payment._id).then(({ data }) => {
      objectUrl = URL.createObjectURL(data);
      setPdfPreviewUrl(objectUrl);
    }).catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [payment?._id]);

  const handlePrint = () => {
    if (pdfPreviewUrl) { window.open(pdfPreviewUrl, '_blank'); return; }
    window.print();
  };

  const [sharing, setSharing] = useState(false);
  const receiptUrl = payment?._id ? getPublicReceiptUrl(payment) : null;
  const shareText = payment && receiptUrl
    ? `${payment.status === 'completed' ? 'Payment Receipt' : 'Bill'}\nName: ${customer.name}\n${payment.status === 'completed' ? 'Receipt No.' : 'B.No.'}: ${payment.receiptNumber}\nAmount: ₹${fmt(totalAmount)}\n${receiptUrl}`
    : '';
  const waLinkFor = (text) => `https://wa.me/91${String(customer.mobile || '').replace(/\D/g, '').slice(-10)}?text=${encodeURIComponent(text)}`;

  const shareLinkViaWhatsApp = () => {
    if (!customer.mobile) { toast.error('Customer has no mobile number'); return; }
    window.open(waLinkFor(shareText), '_blank');
  };

  // Web Share API with an actual file only works on browsers/OSes that support sharing
  // files (mostly mobile Chrome/Android) — elsewhere we fall back to just opening the PDF
  // so the admin can save it and attach it manually inside WhatsApp Web/Desktop.
  const sharePdfViaWhatsApp = async () => {
    if (!payment?._id) return;
    setSharing(true);
    try {
      const { data } = await paymentAPI.getReceiptBlob(payment._id);
      const file = new File([data], `${payment.receiptNumber || 'receipt'}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Receipt', text: shareText });
      } else {
        const url = URL.createObjectURL(data);
        window.open(url, '_blank');
        toast('PDF opened in a new tab — save it and attach it in WhatsApp.', { icon: 'ℹ️' });
      }
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Failed to share PDF');
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <style>{`
        @media print {
          body > * { display: none !important; }
          .bill-print-root { display: block !important; }
          .bill-no-print { display: none !important; }
          .bill-modal-overlay { position: static !important; background: none !important; padding: 0 !important; }
          .bill-modal-box { box-shadow: none !important; border: none !important; max-width: 100% !important; width: 100% !important; }
        }
        .bill-print-root { display: block; }
      `}</style>

      <div className="bill-print-root">
        <div className="bill-modal-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 9999, padding: '24px 16px', overflowY: 'auto',
        }}>
          <div className="bill-modal-box" style={{
            background: 'var(--cb-ffffff)', borderRadius: 10, width: '100%', maxWidth: 680,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
          }}>
            {/* Modal header – hidden on print */}
            <div className="bill-no-print" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', background: 'var(--cb-1e293b)', color: 'var(--c-ffffff)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={16} />
                <span style={{ fontWeight: 700, fontSize: '.95rem' }}>Generate Bill</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handlePrint} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', background: 'var(--primary)', color: 'var(--c-ffffff)',
                  border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '.85rem',
                }}>
                  <Printer size={14} /> {pdfPreviewUrl ? 'Open PDF' : 'Print Bill'}
                </button>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--c-94a3b8)', cursor: 'pointer' }}>
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Bill body – printed */}
            {payment?._id ? (
              <div className="bill-no-print" style={{ height: '75vh', background: 'var(--cb-525659)' }}>
                {pdfPreviewUrl ? (
                  <iframe src={pdfPreviewUrl} title="Bill PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--c-ffffff)', fontSize: '.85rem' }}>
                    Loading PDF…
                  </div>
                )}
              </div>
            ) : (
            <div style={{ padding: '28px 32px', fontFamily: 'Arial, sans-serif', fontSize: '.9rem', color: 'var(--c-111111)' }}>
              {/* Company Header */}
              <div style={{ textAlign: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid var(--cb-1e293b)' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--c-1e293b)', letterSpacing: '.02em' }}>
                  {displayName}
                </div>
                {displayAddress && (
                  <div style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginTop: 2 }}>{displayAddress}</div>
                )}
                {(displayPhone || displayGstin) && (
                  <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginTop: 2 }}>
                    {displayPhone && `Phone: ${displayPhone}`}
                    {displayPhone && displayGstin && ' | '}
                    {displayGstin && `GSTIN: ${displayGstin}`}
                  </div>
                )}
                <div style={{
                  display: 'inline-block', marginTop: 10,
                  background: 'var(--cb-1e293b)', color: 'var(--c-ffffff)',
                  padding: '3px 18px', borderRadius: 3, fontWeight: 700,
                  fontSize: '.82rem', letterSpacing: '.08em', textTransform: 'uppercase',
                }}>
                  {payment ? 'Payment Receipt' : 'Service Invoice'}
                </div>
              </div>

              {/* Receipt + Customer row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                {/* Left – Bill info */}
                <div>
                  <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--c-6b7280)', marginBottom: 8, letterSpacing: '.06em' }}>Bill Details</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                    <tbody>
                      {payment?.receiptNumber && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)', width: '45%' }}>Receipt No.</td>
                          <td style={{ padding: '3px 0', fontWeight: 700, fontFamily: 'monospace' }}>{payment.receiptNumber}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Date</td>
                        <td style={{ padding: '3px 0', fontWeight: 600 }}>
                          {payment?.paymentDate ? fmtDate(payment.paymentDate) : fmtDate(new Date())}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Service</td>
                        <td style={{ padding: '3px 0', fontWeight: 600 }}>
                          {SERVICE_LABELS[payment?.paymentFor] || payment?.paymentFor?.replace(/_/g, ' ') || 'Broadband'}
                        </td>
                      </tr>
                      {payment?.paymentMethod && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Mode</td>
                          <td style={{ padding: '3px 0', textTransform: 'uppercase', fontWeight: 600 }}>{payment.paymentMethod === 'company_paid' ? paidByLabel(displayName) : payment.paymentMethod.replace(/_/g, ' ')}</td>
                        </tr>
                      )}
                      {payment?.collectedBy?.name && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Collected By</td>
                          <td style={{ padding: '3px 0' }}>{payment.collectedBy.name}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Right – Customer info */}
                <div>
                  <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--c-6b7280)', marginBottom: 8, letterSpacing: '.06em' }}>Customer Details</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                    <tbody>
                      {customer.name && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)', width: '40%' }}>Name</td>
                          <td style={{ padding: '3px 0', fontWeight: 700 }}>{customer.name}</td>
                        </tr>
                      )}
                      {customer.customerId && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Customer ID</td>
                          <td style={{ padding: '3px 0', fontFamily: 'monospace', fontWeight: 600 }}>{customer.customerId}</td>
                        </tr>
                      )}
                      {customer.mobile && (
                        <tr>
                          <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Mobile</td>
                          <td style={{ padding: '3px 0' }}>{customer.mobile}</td>
                        </tr>
                      )}
                      {(() => {
                        // CATV bills show just Society (address.area) + House No. (catv.houseNumber) —
                        // broadband keeps the fuller street address since it has no "society" concept.
                        const addressText = serviceType === 'catv'
                          ? [customer.address?.area, customer.catv?.houseNumber].filter(Boolean).join(' - ')
                          : [customer.address?.line1, customer.address?.area, customer.address?.landmark, customer.address?.city].filter(Boolean).join(', ');
                        return addressText ? (
                          <tr>
                            <td style={{ padding: '3px 0', color: 'var(--c-6b7280)' }}>Address</td>
                            <td style={{ padding: '3px 0' }}>{addressText}</td>
                          </tr>
                        ) : null;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Validity section */}
              <div style={{
                background: 'var(--cb-f0f9ff)', border: '1px solid var(--cb-bae6fd)',
                borderRadius: 6, padding: '14px 18px', marginBottom: 20,
              }}>
                <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--c-0369a1)', marginBottom: 10, letterSpacing: '.06em' }}>
                  Validity Period
                </div>
                <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 4 }}>Valid From</div>
                    <input
                      type="date"
                      className="bill-no-print"
                      value={validFrom}
                      onChange={e => setValidFrom(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid var(--cb-cbd5e1)', borderRadius: 4, fontSize: '.85rem' }}
                    />
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--c-0369a1)', display: 'none' }}
                      className="bill-print-date">
                      {validFrom ? fmtDate(validFrom) : '—'}
                    </div>
                    <div className="bill-no-print" style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', marginTop: 2 }}>
                      {validFrom ? fmtDate(validFrom) : '—'}
                    </div>
                  </div>

                  <div style={{ fontSize: '1.2rem', color: 'var(--c-94a3b8)', fontWeight: 300 }}>→</div>

                  <div>
                    <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 4 }}>Valid Until</div>
                    <input
                      type="date"
                      className="bill-no-print"
                      value={validTo}
                      onChange={e => setValidTo(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid var(--cb-cbd5e1)', borderRadius: 4, fontSize: '.85rem' }}
                    />
                    <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', marginTop: 2 }} className="bill-no-print">
                      {validTo ? fmtDate(validTo) : '—'}
                    </div>
                  </div>

                  {planName && (
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)', marginBottom: 2 }}>Plan / Package</div>
                      <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{planName}</div>
                      {planSpeed && <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>{planSpeed}</div>}
                    </div>
                  )}
                </div>
                {/* Print-only validity display */}
                <style>{`
                  @media print {
                    .bill-validity-print { display: flex !important; }
                    input[type="date"] { display: none !important; }
                  }
                  .bill-validity-print { display: none; }
                `}</style>
                <div className="bill-validity-print" style={{ gap: 24, alignItems: 'center', marginTop: 4 }}>
                  <div>
                    <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>Valid From</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--c-0369a1)' }}>{validFrom ? fmtDate(validFrom) : '—'}</div>
                  </div>
                  <div style={{ fontSize: '1.2rem', color: 'var(--c-94a3b8)' }}>→</div>
                  <div>
                    <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>Valid Until</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--c-0369a1)' }}>{validTo ? fmtDate(validTo) : '—'}</div>
                  </div>
                  {planName && (
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>Plan / Package</div>
                      <div style={{ fontWeight: 700 }}>{planName}</div>
                      {planSpeed && <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)' }}>{planSpeed}</div>}
                    </div>
                  )}
                </div>
              </div>

              {/* Amount breakdown */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                <thead>
                  <tr style={{ background: 'var(--cb-1e293b)', color: 'var(--c-ffffff)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '.8rem', fontWeight: 600 }}>Description</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '.8rem', fontWeight: 600 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payment?.items?.length > 0 ? (
                    payment.items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                        <td style={{ padding: '10px 12px', fontSize: '.88rem' }}>{it.label}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '.88rem' }}>₹{fmt(it.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                      <td style={{ padding: '10px 12px', fontSize: '.88rem' }}>
                        {SERVICE_LABELS[payment?.paymentFor] || 'Service Charge'}
                        {planName ? ` — ${planName}` : ''}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '.88rem' }}>₹{fmt(amount)}</td>
                    </tr>
                  )}
                  {discount > 0 && (
                    <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                      <td style={{ padding: '10px 12px', fontSize: '.88rem', color: 'var(--c-16a34a)' }}>Discount</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '.88rem', color: 'var(--c-16a34a)' }}>-₹{fmt(discount)}</td>
                    </tr>
                  )}
                  {gstPercent > 0 && (
                    <tr style={{ borderBottom: '1px solid var(--cb-e5e7eb)' }}>
                      <td style={{ padding: '10px 12px', fontSize: '.88rem', color: 'var(--c-6b7280)' }}>GST ({gstPercent}%)</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '.88rem', color: 'var(--c-6b7280)' }}>₹{fmt(gstAmount)}</td>
                    </tr>
                  )}
                  <tr style={{ background: 'var(--cb-f8fafc)' }}>
                    <td style={{ padding: '12px 12px', fontWeight: 800, fontSize: '1rem' }}>Total Amount</td>
                    <td style={{ padding: '12px 12px', textAlign: 'right', fontWeight: 800, fontSize: '1.1rem', color: 'var(--c-16a34a)' }}>₹{fmt(totalAmount)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Footer */}
              <div style={{ borderTop: '1px solid var(--cb-e5e7eb)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>
                  This is a computer-generated {payment ? 'receipt' : 'invoice'}. No signature required.
                </div>
                {payment?.status === 'completed' && (
                  <div style={{
                    border: '2px solid var(--cb-16a34a)', color: 'var(--c-16a34a)',
                    borderRadius: 4, padding: '2px 12px', fontWeight: 800,
                    fontSize: '.85rem', letterSpacing: '.08em', transform: 'rotate(-8deg)',
                  }}>
                    PAID
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Bottom print bar – hidden on print */}
            <div className="bill-no-print" style={{
              padding: '12px 20px', background: 'var(--cb-f8fafc)', borderTop: '1px solid var(--cb-e5e7eb)',
              display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap',
            }}>
              {payment?._id && (
                <>
                  <button onClick={shareLinkViaWhatsApp} title="Share receipt link via WhatsApp" style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 18px', background: 'var(--cb-25d366)', color: 'var(--c-ffffff)',
                    border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '.85rem',
                  }}>
                    <MessageCircle size={14} /> Share Link
                  </button>
                  <button onClick={sharePdfViaWhatsApp} disabled={sharing} title="Share receipt PDF via WhatsApp" style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 18px', background: 'var(--cb-15803d)', color: 'var(--c-ffffff)',
                    border: 'none', borderRadius: 5, cursor: sharing ? 'default' : 'pointer', fontWeight: 600, fontSize: '.85rem',
                    opacity: sharing ? 0.7 : 1,
                  }}>
                    <MessageCircle size={14} /> {sharing ? 'Preparing…' : 'Share PDF'}
                  </button>
                </>
              )}
              <button onClick={onClose} style={{
                padding: '7px 18px', background: 'var(--cb-ffffff)', border: '1px solid var(--cb-d1d5db)',
                borderRadius: 5, cursor: 'pointer', fontSize: '.85rem',
              }}>
                Close
              </button>
              <button onClick={handlePrint} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 18px', background: 'var(--cb-1e293b)', color: 'var(--c-ffffff)',
                border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '.85rem',
              }}>
                <Printer size={14} /> Print Bill
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
